import { useCallback, useEffect, useState } from 'react';
import {
  reauthenticateWithG2,
  redirectToG2,
} from 'services/auth/g2AuthorizationParams';

import clsx from 'clsx';
import { useSession } from 'next-auth/react';

import {
  ResearchBoardDrawer,
  ResearchBoardNotificationView,
  createResearchBoardClient,
} from 'research-board';

import ErrorBoundary from 'components/BuyerIntent/ErrorBoundary';
import CtaButton from 'components/CtaButton';
import Icon from 'components/Icon';
import { useEventContext } from 'context/EventContext';
import {
  consumeResearchBoardPickerIntent,
  getResearchBoardSignInCallbackUrl,
  getResearchBoardUrl,
} from 'helpers/researchBoardNavigation';
import { getResearchBoardProductCanonicalId } from 'helpers/researchBoardProduct';
import { dispatchSaveProductEvent } from 'shared-utils/saveProductAnalytics';

import classes from './Save.module.scss';

const RESEARCH_BOARD_SOURCE = 'getapp_pin';
const researchBoardClient = createResearchBoardClient();
const pinnedProductStateByUser = new Map();
const pinnedProductListeners = new Map();

const getPinnedProductKey = (userId, productCanonicalId) =>
  `${userId}:${productCanonicalId}`;

const loadPinnedProductState = (userId, productCanonicalId) => {
  const key = getPinnedProductKey(userId, productCanonicalId);
  const cachedPinnedProductState = pinnedProductStateByUser.get(key);
  if (cachedPinnedProductState) return cachedPinnedProductState;

  const pinnedProductStateRequest = researchBoardClient
    .listBoards(productCanonicalId)
    .then((boards) =>
      boards
        .filter((board) => board.selected)
        .map(({ id, title }) => ({ id, title }))
    )
    .catch((error) => {
      pinnedProductStateByUser.delete(key);
      throw error;
    });
  pinnedProductStateByUser.set(key, pinnedProductStateRequest);
  return pinnedProductStateRequest;
};

const subscribeToPinnedProduct = (userId, productCanonicalId, listener) => {
  const key = getPinnedProductKey(userId, productCanonicalId);
  const listeners = pinnedProductListeners.get(key) || new Set();
  listeners.add(listener);
  pinnedProductListeners.set(key, listeners);

  return () => {
    listeners.delete(listener);
    if (listeners.size === 0) pinnedProductListeners.delete(key);
  };
};

const updatePinnedProduct = (userId, productCanonicalId, selectedBoards) => {
  pinnedProductStateByUser.set(
    getPinnedProductKey(userId, productCanonicalId),
    Promise.resolve(selectedBoards)
  );

  pinnedProductListeners
    .get(getPinnedProductKey(userId, productCanonicalId))
    ?.forEach((listener) => listener(selectedBoards));
};

/** @type {import("./types").default} */
const ResearchBoardSave = ({
  listing,
  className,
  engagementComponent = 'product_main-card',
  variant = 'text',
  onlyIcon,
  showSaveCount: _showSaveCount,
  ...props
}) => {
  const { data: session, status: authStatus } = useSession();
  const { addEvent, sendEvent } = useEventContext({
    engagementComponent,
  });
  const [isPinned, setIsPinned] = useState(Boolean(listing.is_saved));
  const [selectedBoards, setSelectedBoards] = useState(null);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [isRemoving, setIsRemoving] = useState(false);
  const [notification, setNotification] = useState(null);
  const productCanonicalId = getResearchBoardProductCanonicalId(listing);
  const isAuthenticated = authStatus === 'authenticated';
  const ctaText = onlyIcon ? 'icon-heart' : 'save';
  const researchBoardUserId = session?.user?.externalId;
  const highlyViewedEventString = listing.most_visited
    ? ' | highly-viewed'
    : '';

  useEffect(() => {
    if (
      isAuthenticated &&
      productCanonicalId &&
      consumeResearchBoardPickerIntent(productCanonicalId)
    ) {
      setDrawerOpen(true);
    }
  }, [isAuthenticated, productCanonicalId]);

  useEffect(() => {
    setIsPinned(Boolean(listing.is_saved));
  }, [listing.is_saved]);

  useEffect(() => {
    if (!isAuthenticated) {
      setIsPinned(Boolean(listing.is_saved));
      return undefined;
    }
    if (!researchBoardUserId) {
      setIsPinned(Boolean(listing.is_saved));
      return undefined;
    }
    if (!productCanonicalId) return undefined;

    let isCurrent = true;
    const pinnedProductKey = getPinnedProductKey(
      researchBoardUserId,
      productCanonicalId
    );
    const unsubscribe = subscribeToPinnedProduct(
      researchBoardUserId,
      productCanonicalId,
      (nextSelectedBoards) => {
        setSelectedBoards(nextSelectedBoards);
        setIsPinned(nextSelectedBoards.length > 0);
      }
    );

    const pinnedProductStateRequest = loadPinnedProductState(
      researchBoardUserId,
      productCanonicalId
    );
    pinnedProductStateRequest
      .then((nextSelectedBoards) => {
        if (
          isCurrent &&
          pinnedProductStateByUser.get(pinnedProductKey) ===
            pinnedProductStateRequest
        ) {
          setSelectedBoards(nextSelectedBoards);
          setIsPinned(nextSelectedBoards.length > 0);
        }
      })
      .catch(() => undefined);

    return () => {
      isCurrent = false;
      unsubscribe();
    };
  }, [
    isAuthenticated,
    listing.is_saved,
    productCanonicalId,
    researchBoardUserId,
  ]);

  const sendResearchBoardTelemetry = useCallback(
    ({ action, outcome, productCanonicalId: telemetryProductId, source }) => {
      sendEvent({
        brand: 'getapp',
        name: `research_board_${action}`,
        outcome,
        product_canonical_id: telemetryProductId,
        source,
      });
    },
    [sendEvent]
  );

  const startG2SignIn = useCallback((options, redirect = redirectToG2) => {
    const signInRequest = redirect(options);
    if (signInRequest && typeof signInRequest.catch === 'function') {
      signInRequest.catch(() => {
        setNotification({
          message: 'We could not start G2 sign-in. Please try again.',
          type: 'error',
        });
      });
    }
  }, []);

  const handleTrigger = (event) => {
    event.preventDefault();
    event.stopPropagation();

    if (authStatus === 'loading') return;

    if (!productCanonicalId) {
      sendResearchBoardTelemetry({
        action: 'sync',
        outcome: 'failure',
        productCanonicalId: '',
        source: RESEARCH_BOARD_SOURCE,
      });
      return;
    }

    if (authStatus === 'unauthenticated') {
      dispatchSaveProductEvent(sendEvent, ctaText);
      startG2SignIn({
        callbackUrl: getResearchBoardSignInCallbackUrl(productCanonicalId),
      });
      return;
    }

    // Removing from the only board is unambiguous, so keep the interaction
    // lightweight. Multiple memberships still open the picker so the user can
    // choose which boards to keep. Unknown board state falls back to the
    // picker rather than risking an unintended removal.
    if (selectedBoards?.length === 1 && selectedBoards[0].title) {
      const [selectedBoard] = selectedBoards;
      setIsRemoving(true);
      void researchBoardClient
        .syncProduct({
          productCanonicalId,
          selectedBoardIds: [],
          source: RESEARCH_BOARD_SOURCE,
        })
        .then((result) => {
          const nextSelectedBoards = result.selectedBoardIds.map((id) => ({
            id,
            title: selectedBoards.find((board) => board.id === id)?.title || '',
          }));
          setSelectedBoards(nextSelectedBoards);
          setIsPinned(result.pinned);
          if (researchBoardUserId) {
            updatePinnedProduct(
              researchBoardUserId,
              productCanonicalId,
              nextSelectedBoards
            );
          }
          setNotification({
            message: `Removed from ${selectedBoard.title}`,
            type: 'success',
          });
          sendResearchBoardTelemetry({
            action: 'sync',
            outcome: 'success',
            productCanonicalId,
            source: RESEARCH_BOARD_SOURCE,
          });
        })
        .catch((error) => {
          if (error?.status === 401) {
            setDrawerOpen(false);
            startG2SignIn(undefined, reauthenticateWithG2);
          } else {
            setNotification({
              message:
                'We could not update your Saved Products. Please try again.',
              type: 'error',
            });
          }
          sendResearchBoardTelemetry({
            action: 'sync',
            outcome: 'failure',
            productCanonicalId,
            source: RESEARCH_BOARD_SOURCE,
          });
        })
        .finally(() => setIsRemoving(false));
      return;
    }

    setDrawerOpen(true);
  };

  const handleAuthenticationRequired = useCallback(() => {
    // A stale Auth.js session must not reopen the picker after reauth. The
    // next click can safely retry with the refreshed session.
    setDrawerOpen(false);
    startG2SignIn(undefined, reauthenticateWithG2);
  }, [startG2SignIn]);

  return (
    <>
      {notification && (
        <ResearchBoardNotificationView
          actionUrl={
            notification.action
              ? getResearchBoardUrl(notification.action.boardId)
              : undefined
          }
          accentColor="#009c9c"
          actionColor="#007f86"
          notification={notification}
          onDismiss={() => setNotification(null)}
        />
      )}
      <CtaButton
        aria-disabled={
          authStatus === 'loading' || !productCanonicalId || isRemoving
        }
        aria-label={isPinned ? 'Edit Saved Products' : 'Save to Saved Products'}
        disabled={authStatus === 'loading' || !productCanonicalId || isRemoving}
        onClick={handleTrigger}
        variant={variant}
        className={clsx(
          classes.saveButton,
          { [classes.onlyIcon]: onlyIcon },
          className
        )}
        title={
          productCanonicalId
            ? undefined
            : 'Pinning is unavailable for this product'
        }
        {...props}
        {...addEvent({
          brand: 'getapp',
          engagementDestination: isPinned
            ? 'user-preferences_deselect-save'
            : 'user-preferences_select-save',
          engagementDetails: `icon_save${highlyViewedEventString}`,
          engagementSource: RESEARCH_BOARD_SOURCE,
          name: 'engagement_save',
          productClickUUID: listing.global_product_id,
          source: RESEARCH_BOARD_SOURCE,
        })}
      >
        <span className={classes.saveButtonContent}>
          <Icon
            data-testid="product-info-card_saved-icon"
            icon={[isPinned ? 'fas' : 'far', 'heart']}
          />
          <span className={classes.saveButtonText}>
            {isPinned ? 'saved' : 'save'}
          </span>
        </span>
      </CtaButton>

      {productCanonicalId && (
        <ResearchBoardDrawer
          client={researchBoardClient}
          onAuthenticationRequired={handleAuthenticationRequired}
          onClose={() => setDrawerOpen(false)}
          onNotification={(nextNotification) => {
            if (nextNotification.type === 'success') {
              setNotification(nextNotification);
            }
          }}
          onSaved={(result, savedBoards) => {
            const selectedBoardById = new Map(
              [...(selectedBoards || []), ...savedBoards].map((board) => [
                board.id,
                board,
              ])
            );
            const nextSelectedBoards = result.selectedBoardIds.map((id) => ({
              id,
              title: selectedBoardById.get(id)?.title || '',
            }));
            setSelectedBoards(nextSelectedBoards);
            setIsPinned(result.pinned);
            if (researchBoardUserId) {
              updatePinnedProduct(
                researchBoardUserId,
                productCanonicalId,
                nextSelectedBoards
              );
            }
          }}
          onTelemetry={sendResearchBoardTelemetry}
          open={drawerOpen}
          product={{ canonicalId: productCanonicalId, name: listing.name }}
          source={RESEARCH_BOARD_SOURCE}
          theme={{ accentColor: '#009c9c', panelWidth: '850px' }}
        />
      )}
    </>
  );
};

const Save = (props) => (
  // Saving is a non-essential control on pages whose primary content is the
  // listing itself, so a failure here degrades to "no button" instead of
  // taking down the surrounding page.
  <ErrorBoundary>
    <ResearchBoardSave {...props} />
  </ErrorBoundary>
);

export default Save;
